import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import Toast from '../components/Toast';

const ACTIVITY_LEVELS = [
  { value: '', label: 'Select activity level' },
  { value: 'sedentary', label: 'Sedentary (little or no exercise)' },
  { value: 'light', label: 'Lightly Active (1-3 days/week)' },
  { value: 'moderate', label: 'Moderately Active (3-5 days/week)' },
  { value: 'very', label: 'Very Active (6-7 days/week)' },
  { value: 'extra', label: 'Extra Active (intense daily)' }
];

const ACTIVITY_FACTORS = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  very: 1.725,
  extra: 1.9
};

const GOAL_TYPES = [
  { value: '', label: 'Select goal' },
  { value: 'lose', label: 'Lose Weight (-500 cal)' },
  { value: 'maintain', label: 'Maintain Weight' },
  { value: 'gain', label: 'Gain Weight (+300 cal)' }
];

const GOAL_ADJUSTMENTS = {
  lose: -500,
  maintain: 0,
  gain: 300
};

function Settings() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();

  // Profile
  const [firstName, setFirstName] = useState(user?.first_name || '');

  // Goals
  const [calorieGoal, setCalorieGoal] = useState(user?.daily_calorie_goal || 2000);
  const [proteinGoal, setProteinGoal] = useState(user?.protein_goal || '');
  const [carbsGoal, setCarbsGoal] = useState(user?.carbs_goal || '');
  const [fatGoal, setFatGoal] = useState(user?.fat_goal || '');

  // Water
  const [waterGoal, setWaterGoal] = useState(user?.water_goal || 2000);
  const [waterUnit, setWaterUnit] = useState('ml');

  // Body stats
  const [age, setAge] = useState(user?.age || '');
  const [weight, setWeight] = useState(user?.weight || '');
  const [height, setHeight] = useState(user?.height || '');
  const [activityLevel, setActivityLevel] = useState(user?.activity_level || '');
  const [goalType, setGoalType] = useState(user?.goal_type || '');
  const [sex, setSex] = useState(user?.sex || '');

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [tdeeResult, setTdeeResult] = useState(null);
  const [toast, setToast] = useState(null);

  const clearMessages = () => {
    setSaveMsg('');
    setSaveErr('');
  };

  const handleSaveProfile = async () => {
    clearMessages();
    setSaving(true);
    try {
      const res = await api.put('/api/users/profile', {
        first_name: firstName,
        daily_calorie_goal: parseInt(calorieGoal) || 2000,
        protein_goal: parseInt(proteinGoal) || null,
        carbs_goal: parseInt(carbsGoal) || null,
        fat_goal: parseInt(fatGoal) || null,
        water_goal: parseInt(waterGoal) || 2000,
        age: parseInt(age) || null,
        weight: parseFloat(weight) || null,
        height: parseFloat(height) || null,
        activity_level: activityLevel || null,
        goal_type: goalType || null,
        sex: sex || null
      });
      updateUser(res.data);
      setSaveMsg('Settings saved');
      setToast({ message: 'Settings saved', type: 'success' });
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveErr(err.response?.data?.error || 'Failed to save');
      setToast({ message: err.response?.data?.error || 'Failed to save', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwMsg('');
    setPwErr('');

    if (!currentPassword || !newPassword) {
      setPwErr('Fill in all password fields');
      return;
    }
    if (newPassword.length < 6) {
      setPwErr('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwErr('New passwords do not match');
      return;
    }

    setPwSaving(true);
    try {
      await api.put('/api/users/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      setPwMsg('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwMsg(''), 3000);
    } catch (err) {
      setPwErr(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const calculateTDEE = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseInt(age);

    if (!w || !h || !a || !activityLevel || !sex) {
      setTdeeResult({ error: 'Fill in age, weight, height, sex, and activity level' });
      return;
    }

    // Convert lbs to kg, inches to cm
    const weightKg = w * 0.453592;
    const heightCm = h * 2.54;

    // Mifflin-St Jeor
    let bmr;
    if (sex === 'male') {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * a) + 5;
    } else {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * a) - 161;
    }

    const factor = ACTIVITY_FACTORS[activityLevel] || 1.2;
    const tdee = Math.round(bmr * factor);
    const adjustment = GOAL_ADJUSTMENTS[goalType] || 0;
    const suggested = Math.max(1200, tdee + adjustment);

    // Macro suggestions: 30% protein, 40% carbs, 30% fat
    const suggestedProtein = Math.round((suggested * 0.30) / 4);
    const suggestedCarbs = Math.round((suggested * 0.40) / 4);
    const suggestedFat = Math.round((suggested * 0.30) / 9);

    setTdeeResult({ bmr: Math.round(bmr), tdee, suggested, adjustment, suggestedProtein, suggestedCarbs, suggestedFat });
  };

  const applyTDEE = () => {
    if (tdeeResult?.suggested) {
      setCalorieGoal(tdeeResult.suggested);
      setProteinGoal(tdeeResult.suggestedProtein);
      setCarbsGoal(tdeeResult.suggestedCarbs);
      setFatGoal(tdeeResult.suggestedFat);
      setTdeeResult(null);
    }
  };

  return (
    <div className="page settings-page">
      <h2>Settings</h2>

      {/* Success / Error banners */}
      {saveMsg && <div className="settings-success">{saveMsg}</div>}
      {saveErr && <div className="error-message">{saveErr}</div>}

      {/* ---- Profile ---- */}
      <div className="settings-section">
        <h3 className="settings-section-title">Profile</h3>
        <div className="card">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="settings-disabled-input"
            />
          </div>
        </div>
      </div>

      {/* ---- Goals ---- */}
      <div className="settings-section">
        <h3 className="settings-section-title">Daily Goals</h3>
        <div className="card">
          <div className="form-group">
            <label>Calorie Goal</label>
            <input
              type="number"
              value={calorieGoal}
              onChange={(e) => setCalorieGoal(e.target.value)}
              min="800"
              max="10000"
              placeholder="2000"
            />
          </div>
          <div className="settings-macro-row">
            <div className="form-group">
              <label>Protein (g)</label>
              <input
                type="number"
                value={proteinGoal}
                onChange={(e) => setProteinGoal(e.target.value)}
                placeholder="--"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Carbs (g)</label>
              <input
                type="number"
                value={carbsGoal}
                onChange={(e) => setCarbsGoal(e.target.value)}
                placeholder="--"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Fat (g)</label>
              <input
                type="number"
                value={fatGoal}
                onChange={(e) => setFatGoal(e.target.value)}
                placeholder="--"
                min="0"
              />
            </div>
          </div>
          <div className="settings-water-goal">
            <div className="settings-water-goal-header">
              <label>Water Goal</label>
              <div className="water-unit-toggle">
                <button
                  className={`water-unit-btn ${waterUnit === 'ml' ? 'active' : ''}`}
                  onClick={() => setWaterUnit('ml')}
                  type="button"
                >ml</button>
                <button
                  className={`water-unit-btn ${waterUnit === 'cups' ? 'active' : ''}`}
                  onClick={() => setWaterUnit('cups')}
                  type="button"
                >cups</button>
              </div>
            </div>
            {waterUnit === 'ml' ? (
              <input
                type="number"
                value={waterGoal}
                onChange={(e) => setWaterGoal(e.target.value)}
                min="500"
                max="10000"
                step="100"
                placeholder="2000"
              />
            ) : (
              <input
                type="number"
                value={Math.round((parseInt(waterGoal) || 2000) / 237 * 10) / 10}
                onChange={(e) => setWaterGoal(Math.round(parseFloat(e.target.value) * 237))}
                min="2"
                max="42"
                step="0.5"
                placeholder="8.4"
              />
            )}
            <span className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              {waterUnit === 'ml'
                ? `${Math.round((parseInt(waterGoal) || 2000) / 237 * 10) / 10} cups`
                : `${parseInt(waterGoal) || 2000} ml`}
            </span>
          </div>
        </div>
      </div>

      {/* ---- Body Stats / TDEE ---- */}
      <div className="settings-section">
        <h3 className="settings-section-title">Body Stats</h3>
        <div className="card">
          <div className="settings-body-row">
            <div className="form-group">
              <label>Sex</label>
              <select value={sex} onChange={(e) => setSex(e.target.value)}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label>Age</label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="--"
                min="10"
                max="120"
              />
            </div>
          </div>
          <div className="settings-body-row">
            <div className="form-group">
              <label>Weight (lbs)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="--"
                min="50"
                step="0.1"
              />
            </div>
            <div className="form-group">
              <label>Height (inches)</label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="--"
                min="36"
                step="0.5"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Activity Level</label>
            <select value={activityLevel} onChange={(e) => setActivityLevel(e.target.value)}>
              {ACTIVITY_LEVELS.map(l => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Goal</label>
            <select value={goalType} onChange={(e) => setGoalType(e.target.value)}>
              {GOAL_TYPES.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          <button
            className="btn btn-outline btn-full"
            onClick={calculateTDEE}
            style={{ marginTop: '0.5rem' }}
          >
            Calculate Suggested Calories
          </button>

          {tdeeResult?.error && (
            <p className="text-muted" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              {tdeeResult.error}
            </p>
          )}

          {tdeeResult && !tdeeResult.error && (
            <div className="tdee-result">
              <div className="tdee-row">
                <span className="text-muted">BMR</span>
                <span>{tdeeResult.bmr} cal</span>
              </div>
              <div className="tdee-row">
                <span className="text-muted">TDEE</span>
                <span>{tdeeResult.tdee} cal</span>
              </div>
              {tdeeResult.adjustment !== 0 && (
                <div className="tdee-row">
                  <span className="text-muted">Goal Adjustment</span>
                  <span>{tdeeResult.adjustment > 0 ? '+' : ''}{tdeeResult.adjustment} cal</span>
                </div>
              )}
              <div className="tdee-row tdee-suggested">
                <span>Suggested Daily Goal</span>
                <span className="tdee-suggested-value">{tdeeResult.suggested} cal</span>
              </div>
              <div className="tdee-macros">
                <div className="tdee-macro-item">
                  <span className="tdee-macro-value">{tdeeResult.suggestedProtein}g</span>
                  <span className="tdee-macro-label">Protein</span>
                  <span className="tdee-macro-pct">30%</span>
                </div>
                <div className="tdee-macro-item">
                  <span className="tdee-macro-value">{tdeeResult.suggestedCarbs}g</span>
                  <span className="tdee-macro-label">Carbs</span>
                  <span className="tdee-macro-pct">40%</span>
                </div>
                <div className="tdee-macro-item">
                  <span className="tdee-macro-value">{tdeeResult.suggestedFat}g</span>
                  <span className="tdee-macro-label">Fat</span>
                  <span className="tdee-macro-pct">30%</span>
                </div>
              </div>
              <button className="btn btn-primary btn-full" onClick={applyTDEE} style={{ marginTop: '0.75rem' }}>
                Use Suggested Macros
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---- Save Button ---- */}
      <button
        className="btn btn-primary btn-full btn-lg"
        onClick={handleSaveProfile}
        disabled={saving}
        style={{ marginBottom: '1.5rem' }}
      >
        {saving ? 'Saving...' : 'Save All Settings'}
      </button>

      {/* ---- Custom Foods ---- */}
      <div className="settings-section">
        <h3 className="settings-section-title">My Foods</h3>
        <button
          className="btn btn-outline btn-full settings-link-btn"
          onClick={() => navigate('/custom-foods')}
        >
          Manage Custom Foods &rarr;
        </button>
      </div>

      {/* ---- Password ---- */}
      <div className="settings-section">
        <h3 className="settings-section-title">Change Password</h3>
        <div className="card">
          {pwMsg && <div className="settings-success">{pwMsg}</div>}
          {pwErr && <div className="error-message">{pwErr}</div>}
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
          <button
            className="btn btn-outline btn-full"
            onClick={handleChangePassword}
            disabled={pwSaving}
          >
            {pwSaving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* ---- Logout ---- */}
      <div className="settings-section">
        <button className="btn btn-danger btn-full" onClick={logout}>
          Log Out
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Settings;
